import type { AdminUser } from '@l2u/web-shared';
import {
  activeTab,
  initials,
  normalisePath,
  type MobileTab,
  type MobileTabKey,
} from '@l2u/web-shared';
import type { Branding } from '@l2u/shared/schemas';
import { NAV_LABEL_PATHS } from './adminNav.ts';

/**
 * The ADMIN app's mobile navigation model — its tab set, its route titles, and
 * the two route predicates only its routes can answer.
 *
 * The pathname primitives this builds on (`MobileTab`, `activeTab`, `initials`)
 * moved to packages/web-shared/src/lib/mobileNav.ts when the driver portal
 * gained the same chrome. They are re-exported here so the ten call sites that
 * already import them from this module keep working, and so there stays one
 * obvious place to look for "the mobile nav model" from inside the admin app.
 */
export { activeTab, initials };
export type { MobileTab, MobileTabKey };

const ADMIN_TABS: MobileTab[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    to: '/',
    icon: 'dashboard',
    end: true,
    extraPrefixes: [],
  },
  {
    key: 'audits',
    label: 'Audits',
    to: '/audits',
    icon: 'clipboard-check',
    end: false,
    extraPrefixes: [],
    // Spoken after the tab label with the badge count: "Audits (2 open audits)".
    badgeLabel: 'open audits',
  },
  {
    key: 'assets',
    label: 'Assets',
    to: '/assets',
    icon: 'truck',
    end: false,
    extraPrefixes: [],
  },
  {
    key: 'actions',
    label: 'Actions',
    to: '/corrective-actions',
    icon: 'alert-triangle',
    end: false,
    extraPrefixes: [],
  },
  {
    key: 'more',
    label: 'More',
    to: '/more',
    icon: 'list',
    end: false,
    extraPrefixes: [],
    // The review queue and document verifications behind this tab — see
    // lib/pendingWork.ts.
    badgeLabel: 'items need your action',
  },
];

/**
 * The ordered bottom-tab descriptors for a signed-in user's role.
 *
 * Every remaining role gets the same five tabs. Kept as a function rather than
 * exporting the array: it is the tested seam callers already go through, so a
 * future per-role tab set needs no change at the call sites.
 */
export function tabsForRole(_role: AdminUser['role']): MobileTab[] {
  return ADMIN_TABS;
}

/**
 * True on the audit-runner route, where the mobile stepper owns the whole
 * screen: the design drops the tab bar there so the sticky prev/next footer is
 * the only bottom chrome and a mid-audit tab tap can't silently abandon marks.
 */
export function hideTabBar(pathname: string): boolean {
  // Root-relative: useLocation() reports the path with the router basename
  // already stripped, so this matches identically in both host modes.
  return /^\/audits\/[^/]+$/.test(normalisePath(pathname));
}

// Titles for the mobile app bar, which replaces the per-page <h1> below `lg`.
// Every route registered in App.tsx appears here; anything missing is a 404.
const ROUTE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/audits': 'Audits',
  '/assets': 'Assets',
  '/corrective-actions': 'Corrective actions',
  '/more': 'More',
  '/companies': 'Companies',
  '/drivers': 'Drivers',
  '/verifications': 'Document verification',
  '/visitors': 'Visitors',
  '/expiring-documents': 'Expiring documents',
  '/questionnaires': 'Questionnaires',
  '/questions': 'Question builder',
  '/documents': 'Documents',
  '/document-types': 'Document types',
  '/training': 'Training',
  '/library': 'Document library',
  '/reports': 'Reports',
  '/settings': 'Settings',
  '/bulk-email': 'Bulk email',
  // Exact titles win over DETAIL_TITLES, so compose is not titled as a send.
  '/bulk-email/new': 'New bulk email',
};

// Detail routes carry an id we can't name, so they're matched by prefix. Order
// doesn't matter — the prefixes are disjoint.
const DETAIL_TITLES: { prefix: string; title: string }[] = [
  { prefix: '/companies', title: 'Company' },
  { prefix: '/drivers', title: 'Driver' },
  { prefix: '/assets', title: 'Asset' },
  { prefix: '/audits', title: 'Audit' },
  { prefix: '/bulk-email', title: 'Bulk email' },
];

function detailTitle(path: string): string | null {
  for (const { prefix, title } of DETAIL_TITLES) {
    if (path.startsWith(`${prefix}/`)) return title;
  }
  return null;
}

/**
 * App-bar title for a pathname, or null when the route is unknown (the caller
 * decides what an unrecognised route shows — today that's the NotFound page,
 * which renders outside the shell anyway).
 *
 * `branding` is optional so the many single-argument callers keep reading the
 * defaults. The two renameable list titles (spec 2026-08-16) follow the tenant's
 * nav labels; the detail titles ('Company', 'Driver') deliberately don't — the
 * scope is the two nav labels only, and a detail title is a record type rather
 * than a nav destination.
 */
export function appBarTitle(pathname: string, branding?: Partial<Branding>): string | null {
  const path = normalisePath(pathname);
  const key = NAV_LABEL_PATHS[path];
  if (key) {
    const override = branding?.navLabels?.[key];
    if (typeof override === 'string' && override.trim().length > 0) return override;
  }
  return ROUTE_TITLES[path] ?? detailTitle(path);
}

/**
 * True for the record-detail routes, which are entered from a list and so get a
 * back chevron in the app bar. List and tab destinations don't.
 */
export function isDetailRoute(pathname: string): boolean {
  return detailTitle(normalisePath(pathname)) !== null;
}

// The "corrective actions awaiting review" count (the tab bar's red badge and
// the dashboard banner) has no key of its own any more: it is the Corrective
// Actions page's 'responded' tab, adminQueries.correctiveActions('responded'),
// read through `select` — one entry for the badge, the banner and the page.
